import { getAdminOrgId } from "./admin";
import { extractApiErrorMessage, getApiBase, getAuthHeaders, BACKEND_INVENTORY_UNAVAILABLE_MSG } from "./auth";

const API_BASE = getApiBase();
function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type Supplier = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  country: string | null;
  status: string;
  note: string | null;
  default_currency: string;
  created_at: string;
  updated_at: string;
};

export type SupplierCreate = {
  name: string;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  status?: string;
  note?: string | null;
  default_currency?: string;
};

export type SupplierUpdate = Partial<SupplierCreate>;

export type SupplierContact = {
  id: string;
  supplier_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type SupplierContactCreate = {
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  is_primary?: boolean;
  notes?: string | null;
};

export type SupplierProduct = {
  id: string;
  supplier_id: string;
  product_id: string;
  supplier_sku: string;
  supplier_product_name: string | null;
  /** Catalog product name (from Product.name), when listed for display */
  product_name?: string | null;
  product_code?: string | null;
  product_type?: string | null;
  unit_cost: number;
  currency: string;
  lead_time_days: number | null;
  min_order_qty: number | null;
  pack_size: number | null;
  is_preferred: boolean;
  is_active: boolean;
  cost_type: string | null;
  created_at: string;
  updated_at: string;
};

export type SupplierProductCreate = {
  product_id: string;
  supplier_sku: string;
  supplier_product_name?: string | null;
  unit_cost: number;
  currency?: string;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  pack_size?: number | null;
  is_preferred?: boolean;
  is_active?: boolean;
  cost_type?: string | null;
};

export type SupplierProductUpdate = {
  supplier_sku?: string;
  supplier_product_name?: string | null;
  unit_cost?: number;
  currency?: string;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  pack_size?: number | null;
  is_preferred?: boolean;
  is_active?: boolean;
  cost_type?: string | null;
};

export async function listSuppliers(params?: { status?: string; query?: string; page?: number; page_size?: number }): Promise<Supplier[]> {
  const orgId = await getAdminOrgId();
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.query) qs.set("q", params.query);
  if (params?.page) qs.set("page", String(params.page));
  if (params?.page_size) qs.set("page_size", String(params.page_size));
  const res = await fetch(`${API_BASE}/suppliers?${qs}`, { headers: defaultHeaders(orgId) });
  if (!res.ok) {
    if (res.status === 404 || res.status === 484) throw new Error(BACKEND_INVENTORY_UNAVAILABLE_MSG);
    throw new Error(await extractApiErrorMessage(res, "Failed to load suppliers"));
  }
  return res.json();
}

export async function getSupplier(id: string): Promise<Supplier> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${id}`, { headers: defaultHeaders(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load supplier"));
  return res.json();
}

export async function createSupplier(payload: SupplierCreate): Promise<Supplier> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const msg = await extractApiErrorMessage(res, "Erreur lors de l'enregistrement du fournisseur.");
    if (res.status === 404) {
      throw new Error("Service fournisseurs indisponible (404). Vérifiez que le backend est bien déployé et à jour.");
    }
    throw new Error(msg);
  }
  return res.json();
}

export async function updateSupplier(id: string, payload: SupplierUpdate): Promise<Supplier> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${id}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to update supplier"));
  return res.json();
}

export async function listSupplierContacts(supplierId: string): Promise<SupplierContact[]> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${supplierId}/contacts`, { headers: defaultHeaders(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load contacts"));
  return res.json();
}

export async function createSupplierContact(supplierId: string, payload: SupplierContactCreate): Promise<SupplierContact> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${supplierId}/contacts`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to create contact"));
  return res.json();
}

export async function updateSupplierContact(supplierId: string, contactId: string, payload: Partial<SupplierContactCreate>): Promise<SupplierContact> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${supplierId}/contacts/${contactId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to update contact"));
  return res.json();
}

export async function deleteSupplierContact(supplierId: string, contactId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${supplierId}/contacts/${contactId}`, { method: "DELETE", headers: defaultHeaders(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to delete contact"));
}

export async function listSupplierProducts(
  supplierId: string,
  options?: { includeCatalogBundles?: boolean }
): Promise<SupplierProduct[]> {
  const orgId = await getAdminOrgId();
  const qs = new URLSearchParams();
  if (options?.includeCatalogBundles) qs.set("include_catalog_bundles", "true");
  const query = qs.toString();
  const res = await fetch(
    `${API_BASE}/suppliers/${supplierId}/products${query ? `?${query}` : ""}`,
    { headers: defaultHeaders(orgId) }
  );
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load supplier products"));
  return res.json();
}

export async function createSupplierProduct(supplierId: string, payload: SupplierProductCreate): Promise<SupplierProduct> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${supplierId}/products`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to add product to supplier"));
  return res.json();
}

export async function updateSupplierProduct(supplierId: string, spId: string, payload: SupplierProductUpdate): Promise<SupplierProduct> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${supplierId}/products/${spId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to update supplier product"));
  return res.json();
}

export async function deleteSupplierProduct(supplierId: string, spId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/suppliers/${supplierId}/products/${spId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to remove supplier from product"));
}
